// Shared vocabulary and value formatting for the site profile's Performance
// tab. Kept out of the panel module so that file exports only components.
//
// The types mirror the payload of
// GET /data/portfolio/site-metrics-golden/<site_key>/ exactly.

export type MetricDirection = "higher" | "lower" | "context";
export type MetricUnit = "count" | "months" | "pct" | "rate" | "ratio";

export type MetricSummary = {
  key: string;
  label: string;
  metric_name: string;
  direction: MetricDirection;
  unit: MetricUnit;
  /** Mean across the trials that reported the metric; null when none did. */
  mean: number | null;
  /** How many of the site's trials contributed — routinely fewer than all. */
  n: number;
  min: number | null;
  max: number | null;
  spread: number | null;
};

export type SiteMetricsDetail = {
  site_key: string;
  n_trials: number;
  index_metrics: MetricSummary[];
  performance: MetricSummary[];
  benchmarks: { n_trials: number; metrics: MetricSummary[] };
  trials: Record<string, number | string | null>[];
};

export function formatMetric(
  value: number | null | undefined,
  unit: MetricUnit,
): string {
  if (value == null) return "—";
  switch (unit) {
    case "ratio":
      return `${value.toFixed(2)}×`;
    case "pct":
      return `${value.toFixed(1)}%`;
    case "months":
      return `${value.toFixed(1)} mo`;
    case "rate":
      return value.toFixed(2);
    default:
      return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }
}

/** "0.50× – 5.00×" for a metric with real spread; null when there is none to show. */
export function rangeLabel(metric: MetricSummary): string | null {
  if (metric.n < 2 || metric.min == null || metric.max == null) return null;
  return `${formatMetric(metric.min, metric.unit)} – ${formatMetric(metric.max, metric.unit)}`;
}

/** An index within this band of 1.0 counts as level with the trial median. */
const AT_MEDIAN_TOLERANCE = 0.05;

/**
 * How the site placed against the trial median, trial by trial — "Beat the
 * median in 8 of 10 trials". A raw min–max says nothing on its own; the count
 * of trials on each side is the reading a user can act on. Falls back to the
 * plain trial count when per-trial values aren't available.
 */
export function indexConsistency(
  metric: MetricSummary,
  trials: Array<Record<string, number | string | null>>,
): string {
  if (metric.n === 0) return "Not reported";
  const values = trials
    .map((t) => t[metric.key])
    .filter((v): v is number => typeof v === "number");
  if (values.length < 2) return "From a single trial";

  const better = values.filter((v) =>
    Math.abs(v - 1) <= AT_MEDIAN_TOLERANCE
      ? false
      : metric.direction === "lower"
        ? v < 1
        : v > 1,
  ).length;

  // "context" metrics have no better/worse, so report placement neutrally
  if (metric.direction === "context") {
    const above = values.filter((v) => v > 1 + AT_MEDIAN_TOLERANCE).length;
    return `Above the median in ${above} of ${values.length} trials`;
  }
  if (better === values.length)
    return `Beat the median in all ${values.length} trials`;
  if (better === 0) return `Below the median in all ${values.length} trials`;
  return `Beat the median in ${better} of ${values.length} trials`;
}

/**
 * Plain-language spread for an unindexed figure — "Ranged 1 to 15 across 10
 * trials". Spelled out because "n = 10 · 1 – 15" reads as debug output.
 */
export function spreadSentence(metric: MetricSummary): string {
  if (metric.n === 0) return "Not reported";
  if (metric.n === 1) return "From a single trial";
  if (metric.min == null || metric.max == null || metric.min === metric.max) {
    return `Same in all ${metric.n} trials`;
  }
  return `Ranged ${formatMetric(metric.min, metric.unit)} to ${formatMetric(
    metric.max,
    metric.unit,
  )} across ${metric.n} trials`;
}

export function directionHint(direction: MetricDirection): string {
  if (direction === "higher") return "Higher is better.";
  if (direction === "lower") return "Lower is better.";
  return "Neither direction is better — read this as context, not performance.";
}
